import { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { useAuth } from '../context/AuthContext';

export const getKeycloakToken = () => {
  try {
    const oidcKey = Object.keys(localStorage).find(k => k.startsWith('oidc.user:'));
    if (!oidcKey) return null;
    
    const raw = localStorage.getItem(oidcKey);
    if (!raw) return null;
    
    const oidcData = JSON.parse(raw);
    return oidcData?.access_token || null;
  } catch (error) {
    console.error("Error retrieving Keycloak token:", error);
    return null;
  }
};

const getSocketURL = () => {
  if (typeof window !== 'undefined') {
    const envUrl = process.env.REACT_APP_API_URL;
    if (envUrl) return envUrl;
    
    const origin = window.location.origin;
    if (/^https?:\/\/(localhost|127\.0\.0\.1)/i.test(origin)) {
      return 'http://localhost:5000';
    }
    
    return 'https://spryclips.com';
  }
  return 'http://localhost:5000';
};

const API_URL = getSocketURL();

let globalSocket = null;
let globalIsConnected = false;
const listeners = new Set();

const useSocket = () => {
  const { user, isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(globalIsConnected);

  useEffect(() => {
    let isMounted = true;

    const handleConnChange = (status) => {
      if (isMounted) setIsConnected(status);
    };
    
    listeners.add(handleConnChange);

    const token = getKeycloakToken();

    if (isAuthenticated && token) {
      if (!globalSocket) {
        globalSocket = io(API_URL, {
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: 5,
          path: '/socket.io/',
          secure: true
        });

        globalSocket.on('connect', () => {
          console.log('Socket connected:', globalSocket.id);
          globalIsConnected = true;
          listeners.forEach(fn => fn(true));
          globalSocket.emit('subscribe:notifications');
        });

        globalSocket.on('disconnect', () => {
          globalIsConnected = false;
          listeners.forEach(fn => fn(false));
        });

        globalSocket.on('connect_error', (error) => {
          console.error('Socket connection error:', error.message);
          globalIsConnected = false;
          listeners.forEach(fn => fn(false));
        });
      } else {
        if (globalSocket.auth.token !== token) {
          globalSocket.auth.token = token;
          globalSocket.disconnect().connect();
        } else if (!globalSocket.connected) {
          globalSocket.connect();
        }
      }
    } else if (!isAuthenticated && globalSocket) {
      globalSocket.disconnect();
      globalSocket = null;
      globalIsConnected = false;
      listeners.forEach(fn => fn(false));
    }

    return () => {
      isMounted = false;
      listeners.delete(handleConnChange);
    };
  }, [isAuthenticated]);

  return { 
    socket: globalSocket, 
    isConnected 
  };
};

export { useSocket };

export const useNotifications = (onNotification) => {
  const { socket } = useSocket();
  useEffect(() => {
    if (!socket) return;
    
    // Capture the current socket reference for cleanup
    const currentSocket = socket;
    
    currentSocket.on('notification', (n) => onNotification && onNotification(n));
    return () => {
      currentSocket.off('notification');
    };
  }, [socket, onNotification]);
};

export const useLiveStream = (streamId, onMessage) => {
  const { socket } = useSocket();
  useEffect(() => {
    if (!socket || !streamId) return;
    
    const currentSocket = socket;
    
    currentSocket.emit('join:livestream', streamId);
    currentSocket.on('livestream:message', (m) => onMessage && onMessage(m));
    return () => {
      currentSocket.emit('leave:livestream', streamId);
      currentSocket.off('livestream:message');
    };
  }, [socket, streamId, onMessage]);
};